/* Volatile — horizontal-carousel position indicator + INFINITE LOOP.
 *
 * Any `[data-card-scroll]` element is treated as a horizontal scroll-
 * snap carousel of `.v-card__scroll-img` children (or similar). On
 * init we clone the FIRST and LAST slide and prepend / append them so
 * the user can scroll past either end and see "the next" image — when
 * scroll lands on a clone, we instantly snap (without animation) to
 * the matching real slide. Net effect: swiping right past the last
 * image lands on the first; swiping left past the first lands on the
 * last. Same behaviour for the click hover-arrows.
 *
 * The position indicator (`[data-card-dots]`, single thumb or legacy
 * segmented bar) reflects the REAL slide index (0..count-1), not the
 * raw scroll position which includes the prepended last-clone.
 *
 * Also re-runs automatically when new scroll containers are inserted
 * (search overlay results, AJAX collection nav, etc.).
 */
(() => {
  const findDots = (scrollEl) => {
    if (!scrollEl) return null;
    if (scrollEl.parentElement) {
      const sibling = scrollEl.parentElement.querySelector(':scope > [data-card-dots]');
      if (sibling) return sibling;
    }
    return scrollEl.querySelector('[data-card-dots]');
  };

  const init = (scrollEl) => {
    if (!scrollEl || scrollEl.__vCarouselInited) return;
    scrollEl.__vCarouselInited = true;
    const dots = findDots(scrollEl);

    /* Real slides at init-time — children of the scroll container
       BEFORE we splice in clones. Capture once so the clone-detect
       logic never confuses a clone for an original. */
    const realSlides = Array.from(scrollEl.children);
    const count = realSlides.length;

    /* ── Infinite loop via cloning. Only when there are >1 real
          slides (single-image cards have nothing to loop). ── */
    let cloned = false;
    if (count > 1) {
      const firstClone = realSlides[0].cloneNode(true);
      firstClone.setAttribute('aria-hidden', 'true');
      firstClone.dataset.carouselClone = 'first';
      const lastClone = realSlides[count - 1].cloneNode(true);
      lastClone.setAttribute('aria-hidden', 'true');
      lastClone.dataset.carouselClone = 'last';
      scrollEl.insertBefore(lastClone, realSlides[0]);
      scrollEl.appendChild(firstClone);
      cloned = true;

      /* Position at the first REAL slide (skip the prepended last-
         clone). Wait one frame so the layout settles before we
         set scrollLeft, then suppress smooth-scroll on the seek. */
      requestAnimationFrame(() => {
        const w = scrollEl.clientWidth;
        if (!w) return;
        const prev = scrollEl.style.scrollBehavior;
        scrollEl.style.scrollBehavior = 'auto';
        scrollEl.scrollLeft = w;
        scrollEl.style.scrollBehavior = prev;
      });
    }

    /* Slider thumb / segmented bar setup — uses REAL count, ignores clones. */
    const thumb = dots ? dots.querySelector('.v-card__progress-thumb') : null;
    const segments = (dots && !thumb) ? Array.from(dots.children) : null;
    const indicatorCount = thumb
      ? Number((dots && dots.dataset.imageCount) || 0)
      : (segments ? segments.length : 0);
    if (thumb && indicatorCount > 1) {
      thumb.style.width = (100 / indicatorCount) + '%';
    }

    /* ── Two listeners:
         • `scroll`     — updates the indicator thumb continuously.
         • `scrollend`  (or a settle-detect fallback) — fires the
                         clone-to-real snap exactly ONCE when the
                         user's swipe momentum has stopped. Snapping
                         during the scroll itself is what was
                         producing the jitter the user reported —
                         the browser was mid-animation when we
                         instantly seeked, so the user saw a flicker.
                         Snapping only at scrollend is invisible. ── */
    const indicatorUpdate = () => {
      const w = scrollEl.clientWidth;
      if (!w) return;
      const rawIdx = Math.round(scrollEl.scrollLeft / w);
      const realIdx = cloned
        ? ((rawIdx - 1) + indicatorCount) % indicatorCount
        : Math.min(indicatorCount - 1, rawIdx);
      if (thumb && indicatorCount > 1) {
        thumb.style.transform = 'translateX(' + (realIdx * 100) + '%)';
      } else if (segments) {
        segments.forEach((seg, idx) => {
          seg.classList.toggle('is-active', idx === realIdx);
        });
      }
    };

    let snapping = false;
    const cloneSnap = () => {
      if (!cloned || snapping) return;
      const w = scrollEl.clientWidth;
      if (!w) return;
      const rawIdx = Math.round(scrollEl.scrollLeft / w);
      let target = null;
      // rawIdx 0 = prepended last-clone → seek to real last (rawIdx count).
      if (rawIdx <= 0) target = w * count;
      // rawIdx count+1 = appended first-clone → seek to real first (rawIdx 1).
      else if (rawIdx >= count + 1) target = w;
      if (target == null) return;
      snapping = true;
      // Disable `scroll-snap` + smooth-scroll for the duration of the
      // instant seek. With scroll-snap active, the browser fights our
      // manual `scrollLeft` set and can pull the position back to the
      // nearest snap point of the OLD slide — that's the "hang" the
      // user reported when trying to scroll from the wrapped first
      // slide to the second. Switching snap off, seeking, then
      // re-enabling on the next frame eliminates that fight entirely. */
      const prevBehavior = scrollEl.style.scrollBehavior;
      const prevSnap = scrollEl.style.scrollSnapType;
      scrollEl.style.scrollBehavior = 'auto';
      scrollEl.style.scrollSnapType = 'none';
      scrollEl.scrollLeft = target;
      // Force a layout flush so the seek commits before scroll-snap
      // is re-enabled.
      // eslint-disable-next-line no-unused-expressions
      scrollEl.offsetWidth;
      indicatorUpdate();
      // Restore the original scroll behavior + snap-type two frames
      // later. One frame for the seek to settle, one more so the
      // next user input lands cleanly on the new position.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollEl.style.scrollBehavior = prevBehavior;
          scrollEl.style.scrollSnapType = prevSnap;
          snapping = false;
        });
      });
    };

    scrollEl.addEventListener('scroll', indicatorUpdate, { passive: true });

    // Settle-detection — snap only after the user (and any in-flight
    // smooth-scroll animation) has fully stopped. Using a debounced
    // settle window of ~180 ms across all browsers, even where
    // `scrollend` is supported, because `scrollend` on Chrome can
    // fire DURING the smooth-scroll deceleration phase, which races
    // with our seek and produces the desktop hang.
    let settleTimer;
    scrollEl.addEventListener('scroll', () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(cloneSnap, 180);
    }, { passive: true });

    if (window.ResizeObserver) {
      new ResizeObserver(indicatorUpdate).observe(scrollEl);
    }
    indicatorUpdate();
  };

  const initAll = (root) => {
    (root || document).querySelectorAll('[data-card-scroll]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Pick up scroll containers added later (search results render, AJAX nav, etc.).
  if (window.MutationObserver) {
    new MutationObserver((muts) => {
      for (const mut of muts) {
        for (const node of mut.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('[data-card-scroll]')) init(node);
          if (node.querySelectorAll) initAll(node);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* Hover-arrow navigation — `<span data-card-nav>` overlay inside
     each card scrolls the carousel by one slide on click. Lives
     inside the wrapping `<a>` (Volatile cards are link-wrapped), so
     we preventDefault + stopPropagation to avoid following the link
     when the user only meant to advance the carousel. The infinite-
     loop snap is handled by the scroll listener above — clicking
     forward at the last image scrolls into the first-clone, then
     gets snapped to the real first; clicking back at the first
     image scrolls into the last-clone, then snapped to the real last. */
  document.addEventListener('click', (e) => {
    const navBtn = e.target.closest('[data-card-nav]');
    if (!navBtn) return;
    e.preventDefault();
    e.stopPropagation();
    const card = navBtn.closest('.v-card');
    if (!card) return;
    const scrollEl = card.querySelector('[data-card-scroll]');
    if (!scrollEl) return;
    const w = scrollEl.clientWidth;
    if (!w) return;
    const dir = navBtn.dataset.cardNav === 'next' ? 1 : -1;
    scrollEl.scrollBy({ left: dir * w, behavior: 'smooth' });
  });
})();
